// ─────────────────────────────────────────────────────────────────────────
// Shared scoring helpers used by Question.jsx, Section.jsx and
// FormBuilder.jsx. Kept framework-free (no React) so they're easy to unit
// test and reuse across the section-level and test-level scoring tabs.
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// The GET /tests/{id} response serializes enums as humanized display text
// ("Single Choice", "Custom", "One") instead of the enum names the rest of
// this app works with ("SINGLE_CHOICE", "CUSTOM", "ONE") — the same names
// used when *creating*/*updating* a question. Every `answerType ===
// "SINGLE_CHOICE"` check in Question.jsx (type-picker highlighting, which
// options editor renders, the "needs defaults" guard) compares against the
// enum name, so loading edit data without normalizing first makes those
// checks silently fail: no options editor renders, the type picker shows
// nothing selected, and clicking a type button — even the "current" one —
// looks like a change and resets that question's options/scores back to
// defaults. Always normalize answerType immediately after
// fetching, before the data reaches Question.jsx or Section.jsx.
// ─────────────────────────────────────────────────────────────────────────
const squashForCompare = (s) =>
  (s || "")
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

/**
 * Map a question's answerType from whatever the backend sent (enum name
 * like "SINGLE_CHOICE" or humanized display text like "Single Choice")
 * to the canonical enum name Question.jsx compares against. Falls back to
 * the raw value unchanged if nothing recognizable matches, so unexpected
 * future values don't get silently mangled.
 */
export const normalizeAnswerType = (rawType, typeList = []) => {
  const target = squashForCompare(rawType);
  if (!target) return rawType;
  const match = typeList.find(
    (t) =>
      squashForCompare(t.value) === target ||
      squashForCompare(t.label) === target,
  );
  return match ? match.value : rawType;
};

/** Highest achievable score for a single question (max of its live options). */
export const getQuestionMaxScore = (question) => {
  const opts = (question?.answerOptions || []).filter((o) => !o.isDeleted);
  if (!opts.length) return 0;
  return Math.max(...opts.map((o) => Number(o.score) || 0));
};

/** Sum of every live question's max score inside one section. */
export const getSectionMaxScore = (section) => {
  const qs = (section?.questions || []).filter((q) => !q.isDeleted);
  return qs.reduce((sum, q) => sum + getQuestionMaxScore(q), 0);
};

/** Sum of every live section's max score — the whole test's ceiling. */
export const getTestMaxScore = (sections) => {
  const secs = (sections || []).filter((s) => !s.isDeleted);
  return secs.reduce((sum, s) => sum + getSectionMaxScore(s), 0);
};

/**
 * Split a 0..total score range into `count` contiguous, non-overlapping
 * bands. Band N's min is always Band N-1's max + 1, so the set is valid
 * the moment it's generated.
 *   generateScoreBands(10, 3) -> [{0,3},{4,7},{8,10}]
 */
export const generateScoreBands = (total, count) => {
  const n = Math.max(1, parseInt(count, 10) || 1);
  const t = Math.max(0, Number(total) || 0);
  const bands = [];
  let prevBoundary = 0;
  for (let i = 1; i <= n; i++) {
    const boundary = Math.round((t * i) / n);
    const min = i === 1 ? 0 : prevBoundary + 1;
    const max = i === n ? t : Math.max(boundary, min);
    bands.push({ minScore: min, maxScore: max });
    prevBoundary = max;
  }
  return bands;
};

/**
 * Keep neighbouring bands contiguous when one boundary is hand-edited.
 * Editing a band's minScore pulls the *previous* (visible) band's maxScore
 * to (newMin - 1); editing a maxScore pushes the *next* band's minScore to
 * (newMax + 1). Deleted rows are skipped when finding "the previous/next"
 * band, so soft-deleted items never get silently rewritten.
 */
export const applyBandSync = (rawList, changedIndex, field, numVal) => {
  const arr = [...rawList];
  if (numVal === null || numVal === undefined || Number.isNaN(numVal))
    return arr;

  const visibleIdx = [];
  arr.forEach((item, i) => {
    if (!item.isDeleted) visibleIdx.push(i);
  });
  const pos = visibleIdx.indexOf(changedIndex);
  if (pos === -1) return arr;

  if (field === "minScore" && pos > 0) {
    const prevRaw = visibleIdx[pos - 1];
    arr[prevRaw] = {
      ...arr[prevRaw],
      maxScore: numVal - 1,
      isChanged: !arr[prevRaw].isNew,
    };
  }
  if (field === "maxScore" && pos < visibleIdx.length - 1) {
    const nextRaw = visibleIdx[pos + 1];
    arr[nextRaw] = {
      ...arr[nextRaw],
      minScore: numVal + 1,
      isChanged: !arr[nextRaw].isNew,
    };
  }
  return arr;
};

export const MAX_RATING_COUNT = 10;
export const DEFAULT_RATING_COUNT = 10;

const blankOption = (overrides = {}) => ({
  id: null,
  answerText: "",
  score: 0,
  isNew: true,
  isChanged: false,
  isDeleted: false,
  ...overrides,
});

export const createSingleChoiceOptions = () => [
  blankOption({ answerText: "Yes", score: 1 }),
  blankOption({ answerText: "No", score: 0 }),
];

export const createMultiChoiceOptions = () => [
  blankOption({ answerText: "", score: 1 }),
];

/**
 * Build `count` rating options (1..MAX_RATING_COUNT). Pass `existing` to
 * preserve any answer text /
 * score already entered for positions that survive the resize; positions
 * beyond the new count are soft-deleted (if previously saved) or dropped
 * (if never saved), mirroring how option deletion works everywhere else.
 */
export const resizeRatingOptions = (count, existing = []) => {
  const n = Math.max(1, Math.min(MAX_RATING_COUNT, parseInt(count, 10) || 1));
  const next = Array.from({ length: n }, (_, i) => {
    const prior = existing[i];
    return prior
      ? {
          ...prior,
          isChanged: !prior.isNew,
        }
      : blankOption({
          answerText: String(i + 1),
          score: i + 1,
        });
  });
  for (let i = n; i < existing.length; i++) {
    const opt = existing[i];
    if (!opt || opt.isDeleted) continue;
    next.push(opt.isNew ? null : { ...opt, isDeleted: true });
  }
  return next.filter(Boolean);
};

/** Fresh set of rating options for a brand-new question. */
export const createRatingOptions = (count = DEFAULT_RATING_COUNT) =>
  resizeRatingOptions(count, []);

/**
 * Keep a pair of same-length band lists (e.g. a section's interpretations
 * and recommendations) aligned 1:1 so the UI can render one "band" = one
 * score range + one interpretation + one recommendation. Pads the shorter
 * list with fresh blank entries; never truncates (deleting is explicit).
 */
export const equalizeBandLists = (
  listA = [],
  listB = [],
  makeBlankA,
  makeBlankB,
) => {
  const visibleA = listA.filter((x) => !x.isDeleted).length;
  const visibleB = listB.filter((x) => !x.isDeleted).length;
  if (visibleA === visibleB) return null;
  if (visibleA > visibleB) {
    const pad = Array.from({ length: visibleA - visibleB }, makeBlankB);
    return { a: listA, b: [...listB, ...pad] };
  }
  const pad = Array.from({ length: visibleB - visibleA }, makeBlankA);
  return { a: [...listA, ...pad], b: listB };
};
